/**
 * Asset Registry — cache, reference counting, and lifecycle management.
 *
 * Two-tier caching: THREE.Cache (file level) + app-level parsed object cache.
 * Reference counting with scoped lifetimes for automatic disposal.
 */

import { Logger } from '../core/Logger.js';
import { disposeObject3D } from './Disposal.js';
import type { EventBus } from '../core/EventBus.js';
import type { LeakDetector } from '../debug/LeakDetector.js';

/**
 * Lightweight event emitter used internally by AssetRegistry for asset change
 * notifications (hot-reload). Kept separate from EventBus so it does not
 * require EngineEventMap typing and stays self-contained.
 */
type AssetChangedHandler = (url: string) => void;

export const enum AssetScope {
  Global = 'global',
  Level = 'level',
  Entity = 'entity',
  Chunk = 'chunk',
  Transient = 'transient',
}

interface AssetEntry {
  url: string;
  asset: unknown;
  refCount: number;
  scope: AssetScope;
  scopeId?: string;
  loadedAt: number;
}

export class AssetRegistry {
  private cache = new Map<string, AssetEntry>();
  private loading = new Map<string, Promise<unknown>>();
  private abortControllers = new Map<string, AbortController>();
  /**
   * Entries evicted from `cache` (e.g. by hot-reload notifyChanged) while still
   * referenced. They are no longer reachable for new loads but must NOT be
   * disposed until their refCount drains, or live consumers would be left
   * pointing at freed GPU resources. release() drains these.
   */
  /** Hot-reload-evicted entries still owed releases, OLDEST first. A second
   *  notifyChanged() before the first drains used to overwrite (orphan) it. */
  private pendingDispose = new Map<string, AssetEntry[]>();
  private leakDetector: LeakDetector | null = null;
  private eventUnsubscribers: (() => void)[] = [];
  private assetChangedHandlers: AssetChangedHandler[] = [];

  get loadedCount(): number {
    return this.cache.size;
  }

  get pendingCount(): number {
    return this.loading.size;
  }

  /**
   * Set a LeakDetector to register loaded assets with.
   */
  setLeakDetector(detector: LeakDetector): void {
    this.leakDetector = detector;
  }

  /**
   * Bind to EventBus for auto-disposal on entity:destroyed and scene:unloaded.
   */
  bindToEventBus(events: EventBus): void {
    this.eventUnsubscribers.push(
      events.on('entity:destroyed', ({ entity }) => {
        this.releaseScope(AssetScope.Entity, String(entity));
      }),
    );
    this.eventUnsubscribers.push(
      events.on('scene:unloaded', ({ name }) => {
        this.releaseScope(AssetScope.Level, name);
      }),
    );
  }

  /**
   * Load an asset. Returns the loaded asset. Deduplicates in-flight requests.
   */
  async load<T>(
    url: string,
    loader: (url: string, signal: AbortSignal) => Promise<T>,
    scope: AssetScope = AssetScope.Global,
    scopeId?: string,
  ): Promise<T> {
    // Check cache
    const cached = this.cache.get(url);
    if (cached) {
      cached.refCount++;
      if (scope !== cached.scope) {
        Logger.warn('Assets', `Scope mismatch for "${url}": cached as ${cached.scope}, requested as ${scope}. Using cached scope.`);
      }
      return cached.asset as T;
    }

    // Check in-flight — deduplicate concurrent loads of the same URL
    const inflight = this.loading.get(url);
    if (inflight) {
      try {
        await inflight;
      } catch {
        // Original load failed — fall through to start a new load
      }
      // Increment ref after load completes — entry may not exist if load failed
      const entry = this.cache.get(url);
      if (entry) {
        entry.refCount++;
        return entry.asset as T;
      }
      // Load failed or was aborted — fall through to start a new load
    }

    // Start new load
    const abortController = new AbortController();
    this.abortControllers.set(url, abortController);

    const promise = loader(url, abortController.signal);
    this.loading.set(url, promise);

    try {
      const asset = await promise;

      // If this load was aborted (or its controller replaced by abort()/a
      // concurrent retry) while awaiting, the awaiter has already been
      // abandoned — nobody will call release() on the entry we'd cache here.
      // A loader that ignores the AbortSignal and resolves anyway would
      // otherwise leak a refCount=1 entry forever. Dispose and don't cache.
      if (abortController.signal.aborted || this.abortControllers.get(url) !== abortController) {
        this._disposeAsset(asset, url);
        Logger.debug('Assets', `Discarded aborted load: ${url}`);
        throw new DOMException(`Load aborted: ${url}`, 'AbortError');
      }

      const entry: AssetEntry = {
        url,
        asset,
        refCount: 1,
        scope,
        scopeId,
        loadedAt: performance.now(),
      };
      this.cache.set(url, entry);

      // Register with leak detector if available
      if (this.leakDetector && asset && typeof asset === 'object') {
        this.leakDetector.register(asset as object, url);
      }

      Logger.debug('Assets', `Loaded: ${url} (scope=${scope})`);
      return asset;
    } catch (err) {
      Logger.error('Assets', `Failed to load: ${url}`, err);
      throw err;
    } finally {
      // Only delete if OUR promise is still the one in the map — a concurrent
      // retry may have already replaced it with a new promise.
      if (this.loading.get(url) === promise) {
        this.loading.delete(url);
      }
      if (this.abortControllers.get(url) === abortController) {
        this.abortControllers.delete(url);
      }
    }
  }

  /**
   * Increment reference count for a loaded asset.
   */
  retain(url: string): void {
    const entry = this.cache.get(url);
    if (entry) entry.refCount++;
  }

  /**
   * Decrement reference count. Disposes when count reaches 0.
   */
  release(url: string, asset?: unknown): void {
    // Drain deferred-dispose entries first. A hot-reload (notifyChanged) evicts
    // a still-referenced asset into pendingDispose; the holders of that old
    // asset are the ones who still owe a release(), so route their releases
    // there before touching any freshly-reloaded cache entry under the same url.
    // This frees the old GPU resources once its refCount drains instead of
    // force-disposing live resources at eviction time.
    const pendingList = this.pendingDispose.get(url);
    // When the caller hands back the asset it holds, route the release to
    // EXACTLY that entry: a holder of the freshly reloaded asset must not
    // drain the old pending entry's count (that disposed the live asset once
    // the old holders finished releasing). Without the asset, fall back to
    // oldest-pending-first.
    if (pendingList && pendingList.length > 0) {
      const idx = asset === undefined ? 0 : pendingList.findIndex((e) => e.asset === asset);
      const live = this.cache.get(url);
      if (idx >= 0 && !(asset !== undefined && live && live.asset === asset)) {
        const pending = pendingList[idx];
        pending.refCount--;
        if (pending.refCount <= 0) {
          this._disposeEntry(pending);
          pendingList.splice(idx, 1);
          if (pendingList.length === 0) this.pendingDispose.delete(url);
        }
        return;
      }
    }

    const entry = this.cache.get(url);
    if (!entry) return;

    entry.refCount--;
    if (entry.refCount <= 0) {
      this._disposeEntry(entry);
      this.cache.delete(url);
    }
  }

  /**
   * Release all assets in a scope. Decrements refCount and only disposes
   * when it reaches 0, so shared assets are not destroyed prematurely.
   */
  releaseScope(scope: AssetScope, scopeId?: string): void {
    const matching: string[] = [];
    for (const [url, entry] of this.cache) {
      if (entry.scope === scope && (!scopeId || entry.scopeId === scopeId)) {
        matching.push(url);
      }
    }
    for (const url of matching) {
      const entry = this.cache.get(url);
      if (!entry) continue;
      entry.refCount--;
      if (entry.refCount <= 0) {
        this._disposeEntry(entry);
        this.cache.delete(url);
      }
    }
  }

  /**
   * Get a loaded asset (null if not loaded).
   */
  get<T>(url: string): T | null {
    return (this.cache.get(url)?.asset as T) ?? null;
  }

  isLoaded(url: string): boolean {
    return this.cache.has(url);
  }

  abort(url: string): void {
    this.abortControllers.get(url)?.abort();
    this.loading.delete(url);
    this.abortControllers.delete(url);
  }

  // ─── Hot-reload support ──────────────────────────────────────────────────────

  /**
   * Subscribe to asset change notifications (emitted by notifyChanged).
   * Returns an unsubscribe function.
   *
   * Used by AssetReloader to detect when a file on disk has changed.
   */
  onChanged(handler: AssetChangedHandler): () => void {
    this.assetChangedHandlers.push(handler);
    return () => {
      const idx = this.assetChangedHandlers.indexOf(handler);
      if (idx !== -1) this.assetChangedHandlers.splice(idx, 1);
    };
  }

  /**
   * Notify listeners that an asset file has changed on disk.
   *
   * Call this from hot-reload adapters (Vite HMR, chokidar watcher) to
   * invalidate cached entries and trigger reloads. The cache entry is
   * evicted so the next `load()` call fetches fresh data.
   *
   * Entity state is NOT affected — only the cached asset data changes.
   */
  notifyChanged(url: string): void {
    // Evict the cached entry so the next load() fetches fresh data.
    const entry = this.cache.get(url);
    if (entry) {
      this.cache.delete(url);
      if (entry.refCount > 0) {
        // Live consumers still hold this asset — disposing now would free GPU
        // resources still referenced by entities. Defer disposal until the
        // refCount drains via release(); the eviction alone makes the next
        // load() fetch fresh data.
        const list = this.pendingDispose.get(url) ?? [];
        list.push(entry);
        this.pendingDispose.set(url, list);
        Logger.debug('Assets', `Cache evicted for hot-reload (dispose deferred, refCount=${entry.refCount}): ${url}`);
      } else {
        this._disposeEntry(entry);
        Logger.debug('Assets', `Cache evicted for hot-reload: ${url}`);
      }
    }

    // Notify all hot-reload listeners
    for (const handler of this.assetChangedHandlers.slice()) {
      try {
        handler(url);
      } catch (err) {
        Logger.error('Assets', `notifyChanged handler error for "${url}": ${err}`);
      }
    }
  }

  dispose(): void {
    for (const unsub of this.eventUnsubscribers) unsub();
    this.eventUnsubscribers.length = 0;
    this.assetChangedHandlers.length = 0;

    for (const [, entry] of this.cache) {
      this._disposeEntry(entry);
    }
    this.cache.clear();
    // Dispose any entries that were evicted by hot-reload but still awaiting
    // refCount drain — on teardown we force-free them.
    for (const [, list] of this.pendingDispose) {
      for (const entry of list) this._disposeEntry(entry);
    }
    this.pendingDispose.clear();
    for (const [, controller] of this.abortControllers) {
      controller.abort();
    }
    this.loading.clear();
    this.abortControllers.clear();
  }

  private _disposeEntry(entry: AssetEntry): void {
    this._disposeAsset(entry.asset, entry.url);
  }

  /** Dispose a raw asset value (shared by entry disposal and aborted-load cleanup). */
  private _disposeAsset(asset: unknown, url: string): void {
    if (!asset || typeof asset !== 'object') return;

    try {
      if ('dispose' in asset && typeof (asset as any).dispose === 'function') {
        (asset as any).dispose();
      }
      // If it's a Three.js Object3D, do deep disposal
      if ('traverse' in asset && typeof (asset as any).traverse === 'function') {
        disposeObject3D(asset as any);
      }
    } catch (err) {
      Logger.warn('Assets', `Dispose error for ${url}: ${err}`);
    }
    // Notify leak detector so it doesn't falsely report this as a leak
    this.leakDetector?.markDisposed?.(asset as object);
    Logger.debug('Assets', `Disposed: ${url}`);
  }
}
